<template>
  <ModalWrapper
    className="city-select-modal"
    :isVisible="isVisible"
    @close="$emit('close')"
    :steps="steps"
    :startAt="startAt"
    :showOnline="showOnline"
    :shouldPersistInDom="shouldPersistInDom"
    :shouldPortal="shouldPortal"
    :should-close-on-select="shouldCloseOnSelect"
    :showOnlineEventsRedirect="showOnlineEventsRedirect"
  />
</template>
<script lang="ts" setup>
import { computed, ref, defineAsyncComponent, defineComponent } from 'vue';
import type { PropType } from 'vue';
import type { Category, Event } from '@/resources/types';
import { useNuxtApp } from '#imports';
import { SOLDOUT_CITY_SLUG } from '@/resources/helpers';
const ModalWrapper = defineAsyncComponent(() => import('@/components/molecules/modals/ModalWrapper.vue'));
const emit = defineEmits(['close', 'citySelected']);
const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  startAt: {
    type: Number,
    default: 0
  },
  event: {
    type: Object as PropType<Event>,
    default: () => {}
  },
  city: {
    type: Object as PropType<Category>,
    default: () => {}
  },
  availableCities: {
    type: Array as PropType<Category[]>,
    default: () => []
  },
  availableDatesInCity: {
    type: Array as PropType<Event[]>,
    default: () => []
  },
  showOnline: {
    type: Boolean,
    default: true
  },
  showSearch: {
    type: Boolean,
    default: true
  },
  shouldPersistInDom: {
    type: Boolean,
    default: false
  },
  shouldPortal: {
    type: Boolean,
    default: true
  },
  shouldHref: {
    type: Boolean,
    default: false
  },
  shouldCloseOnSelect: {
    type: Boolean,
    default: false
  },
  showOnlineEventsRedirect: {
    type: Boolean,
    default: true
  }
});

const { $logger, $i18n } = useNuxtApp();
const selectedCity = ref(props.city);

const steps = computed(() => {
  if (props.city) {
    return [
      {
        title: $i18n.t('category.search_title'),
        content: {
          component: defineAsyncComponent(() => import('@/components/molecules/selects/CitySelector.vue')),
          props: {
            cities: props.availableCities,
            city: selectedCity.value,
            loading: false,
            showOnline: props.showOnline,
            showSearch: props.showSearch,
            shouldHref: props.shouldHref,
            shouldCloseOnSelect: props.shouldCloseOnSelect,
            showOnlineEventsRedirect: props.showOnlineEventsRedirect
          },
          events: {
            change: (cat) => {
              emit('citySelected', cat, false);
              selectedCity.value = cat;
            }
          }
        },
        primaryAction: {
          component: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
          title: $i18n.t('event.select_date'),
          props: {
            text: $i18n.t('event.select_date')
          },
          events: {
            click: () => {
              emit('citySelected', selectedCity.value, true);
            }
          }
        },
        secondaryAction: {
          component: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
          title: $i18n.t('common.save_selection'),
          props: {
            text: $i18n.t('common.save_selection'),
            outlined: true,
            style: 'border:0;'
          },
          events: {
            click: () => {
              emit('citySelected', selectedCity.value);
              emit('close');
              // to prevent the modal to open, we remove the item in localStorage
              localStorage.removeItem(SOLDOUT_CITY_SLUG);
            }
          }
        }
      },
      {
        title: $i18n.t('event.select_date'),
        content: {
          component: defineAsyncComponent(() => import('@/components/molecules/selects/DateSelector.vue')),
          props: {
            events: props.availableDatesInCity,
            city: props.city,
            loading: false
          }
        },
        primaryAction: {
          component: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
          title: $i18n.t('common.save'),
          props: {
            text: $i18n.t('common.save')
          },
          events: {
            click: () => {
              $logger.log('todo: save selection');
            }
          }
        },
        secondaryAction: {
          component: defineAsyncComponent(() => import('@/components/molecules/buttons/SButton.vue')),
          title: $i18n.t('common.back'),
          props: {
            text: $i18n.t('common.back'),
            outlined: true,
            style: 'border:0;'
          },
          events: {
            click: 'goToPreviousStep'
          }
        }
      }
    ];
  }

  return [
    {
      title: $i18n.t('category.search_title'),
      content: {
        component: defineAsyncComponent(() => import('@/components/molecules/selects/CitySelector.vue')),
        props: {
          cities: props.availableCities,
          city: selectedCity.value,
          loading: false,
          showOnline: props.showOnline,
          showSearch: props.showSearch,
          shouldHref: props.shouldHref,
          shouldCloseOnSelect: props.shouldCloseOnSelect,
          showOnlineEventsRedirect: props.showOnlineEventsRedirect
        },
        events: {
          change: (cat) => {
            emit('citySelected', cat, false);
            selectedCity.value = cat;
          }
        }
      }
    },
    {
      title: $i18n.t('event.select_date'),
      content: {
        component: defineAsyncComponent(() => import('@/components/molecules/selects/DateSelector.vue')),
        props: {
          events: props.availableDatesInCity,
          city: props.city,
          loading: false
        }
      }
    }
  ];
});
</script>

<style lang="scss">
.city-select-modal.modal {
  .modal-container {
    max-height: min(90%, 724px) !important;
    max-width: min(100%, 640px) !important;
    height: 100%;
  }

  .modal-footer {
    display: flex;
    flex-direction: row;
    a {
      color: var(--color-primary);
      font-weight: 500;
      text-decoration: underline;
    }
  }
}
</style>

<style lang="scss" scoped></style>
